<template>

  <b-container v-if="this.$root.token">
    <br>
    <h3>Analysis</h3>
    <br>
  
    <b-tabs>
      <b-tab title="Report Configuration" active>
        <span class="col col-sm-4 text-left">
          <a class="clickable" @click="apply()" v-if="!applying">
            <font-awesome-icon icon="poll" size="1x"/> Apply
          </a>
          <font-awesome-icon icon="spinner" size="lg" spin v-if="applying"/>
        </span>
        <span class="col col-sm-6 text-left" v-if="surveyRuns.length === 1">
          <a class='clickable' @click="download(surveyRuns[0])" v-if="!downloading">
            <font-awesome-icon icon="file-download" size="1x"/> Download
          </a>
          <font-awesome-icon icon="spinner" size="lg" spin v-if="downloading"/>
        </span>
        <table class="table">
          <tbody>
            <draggable v-model="dimensions" v-if="!!dimensions">
              <tr v-for="(item, index) in dimensions" :key="index" class="d-flex">
                <th scope="row" class="text-left col-2" ><font-awesome-icon icon="sort" size="1x"/>{{ item.dimensionName }}</th>
                <td class="text-left col-10">
                  <b-row>
                    <b-col cols="3" class="text-left align-left">
                      <input type="checkbox" :value="item.report" v-model="item.report"> <small>Report on this category</small>
                    </b-col>
                    <b-col cols="6" v-if="item.dimensionName !== 'Year' && item.dimensionName !== 'Period'" class="text-right align-right">
                      <input type="checkbox" :value="item.ignoreNotSpecified" v-model="item.ignoreNotSpecified"> <small>Ignore responses where this question was not answered.</small>
                    </b-col>
                    <b-col cols="3" v-if="item.dimensionName !== 'Year' && item.dimensionName !== 'Period'" class="text-right align-right">
                      <input type="checkbox" @change="checkUncheckDimensions(index, $event)"> <small>Check/Uncheck All</small>
                    </b-col>
                  </b-row>
                  <draggable v-model="item.responseValues" v-if="item.report">
                    <li v-for="(element, index1) in item.responseValues" :key="index1" class="list-group-item">
                      <font-awesome-icon icon="sort" size="1x"/>
                      {{ element.text }} reported as <input type="text" v-model="element.as" class="input-lg">
                      <span v-if="item.dimensionName !== 'Year' && item.dimensionName !== 'Period'">
                        <input type="checkbox" v-model="dimensions[index].responseValues[index1].ignore"> <small>Ignore responses with this value</small>
                      </span>
                    </li>
                  </draggable>
                </td>
              </tr>
            </draggable>
          </tbody>
        </table>
      </b-tab>
      <b-tab title="Group Data" v-if="barChartData && !applying">
        <br>
        <score-summary :chart-data="barChartData"/>
      </b-tab>
      <b-tab title="Element Score Graphs" v-if="barChartData && !applying">
        <br>
        <b-row class="text-right align-right">
          <b-col>
            <input type="checkbox" v-model="skipBarTotals"><small>Omit "Overall" bar</small>
          </b-col>
        </b-row>
        <br>
        <score-bar-chart :chart-data="barChartData" :width="700" :height="positiveHeight" :type="'positive'" :programme-name="surveyRuns[0].programmeName" :skip-totals="skipBarTotals" />
        <br>
        <score-bar-chart :chart-data="barChartData" :width="700" :height="negativeHeight" :type="'negative'" :programme-name="surveyRuns[0].programmeName" :skip-totals="skipBarTotals"/>
      </b-tab>
      <b-tab title="Overall Score Graphs" v-if="barChartData && !applying">
        <br>
        <chevron-and-x :width="350" :height="400" :chart-data="overallScore" />
        <br>
        <b-row>
          <b-col cols=8 class="text-right align-right">
            <input type="checkbox" v-model="skipBarOverall">
          </b-col>
          <b-col cols=4 class="text-left align-left">
            <small>Omit "Overall" bar</small>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols=8 class="text-right align-right">
            <input type="checkbox" v-model="yearsAndPeriodsAsBars">
          </b-col>
          <b-col cols=4 class="text-left align-left">
            <small>Display Period + Year as Bars</small>
          </b-col>
        </b-row>
        <br>
        <gear-bar 
          :width="1000" 
          :height="600" 
          :chart-data="barChartData" 
          :overall-score="overallScore" 
          :skip-overall-bar="skipBarOverall"
          :year-dimension="yearDimension"
          :period-dimension="periodDimension"
          :year-dimension-index="yearDimensionIndex"
          :period-dimension-index="periodDimensionIndex"
          :years-and-periods-as-bars="yearsAndPeriodsAsBars"
          :programme-name="surveyRuns[0].programmeName"/>        
      </b-tab>
      <b-tab title="Industry Graphs" v-if="industryAverages && !applying">
        <br>
        <gear-industry-bar :chart-data="industryAverages" :survey-runs="surveyRuns" :width="800" :height="350"/>
        <br>
        <element-industry-line :chart-data="barChartData" :industry-data="industryAverages" :type="'positive'" :width="1000" :height="600" v-if="industryAverages"/>
        <br>
        <element-industry-line :chart-data="barChartData" :industry-data="industryAverages" :type="'negative'" :width="600" :height="600" v-if="industryAverages"/>
      </b-tab>
      <b-tab title="Comments" v-if="barChartData && !applying">
        <comments-list :survey-runs="surveyRuns" :dimensions="dimensionsForComments" show-type-selection/>
      </b-tab>
    </b-tabs>
    <div style="height: 100px"/>
  </b-container>
</template>
<script>
import * as API from "@/services/API";
import draggable from 'vuedraggable';
import ScoreBarChart from '../graphs/ScoreBarChart.js';
import ChevronAndX from '../graphs/ChevronAndX.js';
import GearIndustryBar from '../graphs/GearIndustryBar.js';
import ElementIndustryLine from '../graphs/ElementIndustryLine.js';
import GearBar from '../graphs/GearBar.js';
import ScoreSummary from './ScoreSummary';
import { getDimension, getOverallScore } from '../services/analysis';
import CommentsList from "@/components/CommentsList.vue";
import * as _ from 'lodash';

export default {
  name: 'SurveyAnalysis',
  components: {draggable, ScoreBarChart, ChevronAndX, GearBar, ScoreSummary, GearIndustryBar, ElementIndustryLine, CommentsList},
  props: {
    surveyRuns: {
      default: () => [],
      type: Array
    }},
  data: function () {
    return {
      barChartData: null,
      industryAverages: null,
      downloading: false,
      applying: false,
      positiveHeight: 0,
      negativeHeight: 0,
      dimensionsForComments: [],
      dimensions: [],
      overallScore: 0,
      skipBarTotals: false,
      skipBarOverall: false,
      yearsAndPeriodsAsBars: false
    };
  },
  computed: {
    // spit it out for the API.
    dimensionsToReport: function () {
      return (this.dimensions || []).filter(dimension => dimension.report).map(dimension => ({
        dimensionName: dimension.dimensionName,
        report: true,
        ignoreNotSpecified: dimension.ignoreNotSpecified,
        responseValues: dimension.responseValues.map(responseValue => ({text: responseValue.text, as: responseValue.ignore ? 'IGNORE' : responseValue.as}))
      }));
    },
    yearDimension: function () {
      return this.dimensionsToReport.filter(dimension => dimension.dimensionName === "Year")[0];
    },
    yearDimensionIndex: function () {
      return this.dimensionsToReport.reduce((accumulator, dimension, index) => {
        if (dimension.dimensionName === "Year") return index;
        return accumulator;
      }, -1);
    },
    periodDimension: function () {
      return this.dimensionsToReport.filter(dimension => dimension.dimensionName === "Period")[0];
    },
    periodDimensionIndex: function () {
      return this.dimensionsToReport.reduce((accumulator, dimension, index) => {
        if (dimension.dimensionName === "Period") return index;
        return accumulator;
      }, -1);
    }
  },
  created: function () {
    // something to do with vue and watchers.
    const plainSurveyRuns = JSON.parse(JSON.stringify(this.surveyRuns));

    // put together ALL the dimensions from all the survey runs.
    const keyedDimensions = plainSurveyRuns.reduce((accumulator, surveyRun) => {
      surveyRun.dimensions.forEach(dimension => {
        if (!accumulator[dimension.dimensionName]) {
          accumulator[dimension.dimensionName] = dimension;
          accumulator[dimension.dimensionName].responseValueKeys = dimension.responseValues.map(responseValue => responseValue.text);
          accumulator[dimension.dimensionName].report = false;
        } else {
          dimension.responseValues.forEach(responseValue => {
            if (accumulator[dimension.dimensionName].responseValueKeys.indexOf(responseValue.text) === -1) {
              accumulator[dimension.dimensionName].responseValueKeys.push(responseValue.text);
              accumulator[dimension.dimensionName].responseValues.push(responseValue);
            }
          });
        }
      });
      return accumulator;
    }, {});

    // turn it into the array we want.
    this.dimensions = Object.keys(keyedDimensions).map(key => {
      const value = keyedDimensions[key];
      delete value.responseValueKeys;
      value.responseValues.forEach((responseValue, index) => value.responseValues[index].ignore = false);
      return value;
    });

    // year and period for multi.
    if (this.surveyRuns.length > 1) {
      this.dimensions.unshift({dimensionName: 'Period', report: false, responseValues: _.uniq(plainSurveyRuns.map(surveyRun => surveyRun.period)).map(period => ({text: period, as: period}))});
      this.dimensions.unshift({dimensionName: 'Year', report: false, responseValues: _.uniq(plainSurveyRuns.map(surveyRun => surveyRun.year)).map(year => ({text: year, as: year}))});
    }
  },
  methods: {
    forceFileDownload(response, filename){
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      // no filename in response :(
      link.setAttribute('download', filename) // have to specify a filename here
      document.body.appendChild(link)
      this.downloading = false;
      link.click()
    },
    // download is only for ONE scoring event.
    download: function(surveyRun) {
      this.downloading = true;
      return API.getExcelReport(surveyRun.surveyRunId, this.dimensionsToReport, null, this.$root.token).then(response => {
        this.forceFileDownload(response, `${surveyRun.surveyName.replace(/\s/g, '_')}_auto_analysis.xlsx`)
      })
    },
    // check/uncheck all the values in a dimension
    checkUncheckDimensions: function(index, event) {
      const ignore = event.srcElement.checked;
      this.dimensions[index].responseValues
        .forEach((item, index1) => {
          this.dimensions[index].responseValues[index1].ignore = ignore;
        });  
    },
    apply: function() {
      this.applying = true;
      this.dimensionsForComments = this.dimensionsToReport;
      const promises = [
        API.getScores(this.surveyRuns.map(surveyRun => surveyRun.id), this.dimensionsToReport, this.$root.token)
      ];

      let gettingIndustryAverages = false;
      // industry averages for the programme of the first scoring event.
      if (!this.industryAverages) {
        gettingIndustryAverages = true;
        promises.push(API.getIndustryAverages(this.surveyRuns[0].programmeId, this.$root.token));
      }
      
      return Promise.all(promises)
        .then(results => {
          this.barChartData = results[0].body;
          this.overallScore = this.surveyRuns.length == 1 ? this.surveyRuns[0].overallScore : getOverallScore(results[0].body);
          
          // industry averages
          if (!this.industryAverages ) this.industryAverages = results[1];

          let positiveElements = 0;
          let negativeElements = 0;

          // figure out how many positive and negative elements
          const groupings = [];
          this.barChartData.reduce((accumulator, row) => {
            const dimension = getDimension(row);
            if (groupings.indexOf(dimension) === -1) groupings.push(dimension);
            if (accumulator.indexOf(row.elementName) === -1) {
              if (row.scoreBackwards) {
                negativeElements += 1;
              } else {
                positiveElements += 1;
              }
              accumulator.push(row.elementName);
            }
            return accumulator;
          },[]);

          // figure out how many dimensions
          
          const height = groupings.length > 2 ? 110 : (groupings.length > 1 ? 80 : 40);
          this.positiveHeight = 67 + 5 + (height * positiveElements);
          this.negativeHeight = 67 + 5 + (height * negativeElements);

          this.applying = false;
        });
    }
  }
}
</script>


// WEBPACK FOOTER //
// src/components/SurveyAnalysis.vue